import React, { useState, useEffect } from 'react';
import '../styles/CreateCategoryModal.scss';
import { getData, postData, ApiResponse, getUniqueCategoryName } from '../utils/api';
import WordsList, { WordItem } from '../components/WordsList';
import logger from '../utils/logger';
import { fetchDictionaryCategories, DictionaryCategory } from '../utils/api';
import Notification from './Notification';
import { NOTIFICATION_DURATION } from '../constants/notifications';
import { validateCategoryName } from '../utils/validation';

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated: (categoryName: string) => void;
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
  isOpen,
  onClose,
  onCategoryCreated
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [emoji, setEmoji] = useState('📝');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryName(e.target.value);
    setError(null);
  };

  const handleEmojiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Если введено больше 2 символов, берем последние 2 символа
    const lastChars = value.length > 2 ? value.slice(-2) : value;
    
    // Проверка на наличие обычных текстовых символов (буквы, цифры, пунктуация, пробелы)
    const hasCommonCharacters = /[a-zA-Z0-9.,!?;:'"()\[\]{}\s]/.test(lastChars);
    
    // Если нет обычных символов, считаем это эмодзи
    if (!hasCommonCharacters) {
      setEmoji(lastChars);
    }
  };

  const handleEmojiPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Берем только последние 2 символа из вставленного текста
    const lastChars = pastedText.length > 2 ? pastedText.slice(-2) : pastedText;
    
    // Проверка на наличие обычных текстовых символов
    const hasCommonCharacters = /[a-zA-Z0-9.,!?;:'"()\[\]{}\s]/.test(lastChars);
    
    // Если нет обычных символов, считаем это эмодзи
    if (!hasCommonCharacters) {
      setEmoji(lastChars);
    }
  };

  const handleSubmit = async () => {
    const validationError = validateCategoryName(categoryName);
    if (validationError) {
      setValidationError(validationError);
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);
    setValidationError(null);

    try {
      // Получаем уникальное имя категории
      const uniqueCategoryName = await getUniqueCategoryName(categoryName.trim());

      const response = await postData<any>('/dictionary/categories', {
        name: uniqueCategoryName,
        emoji: emoji || '📝'
      });

      if (response.success) {
        logger.log('Список успешно создан:', uniqueCategoryName);
        onCategoryCreated(uniqueCategoryName);
        onClose();
      } else {
        setError('Что-то серверу нехорошо, попробуй позже');
      }
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Что-то серверу нехорошо, попробуй позже');
      } else {
        const errorMsg = err instanceof Error ? err.message : 'Неизвестная ошибка';
        setError(`Произошла ошибка: ${errorMsg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {error && (
        <Notification
          message={error}
          onClose={() => setError(null)}
          duration={NOTIFICATION_DURATION}
        />
      )}
      <div className="create-category-modal-overlay" onClick={onClose}>
        <div className="create-category-modal" onClick={(e) => e.stopPropagation()}>
          <h2>Создание списка</h2>
          
          <div className="input-row">
            <div className="emoji-container">
              <input
                id="category-emoji"
                type="text"
                value={emoji}
                onChange={handleEmojiChange}
                onPaste={handleEmojiPaste}
                className="emoji-input"
                maxLength={2}
                inputMode="text"
                aria-label="Выберите эмодзи"
                title="Введите эмодзи"
              />
            </div>
            
            <div className="name-container">
              <input
                key={`input-${animationKey}`}
                id="category-name"
                type="text"
                value={categoryName}
                onChange={handleNameChange}
                onKeyPress={handleKeyPress}
                placeholder="Футбол"
                className={`name-input ${validationError ? 'error' : ''}`}
                autoFocus
                disabled={isLoading}
              />
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              className="cancel-button" 
              onClick={onClose}
              disabled={isLoading}
            >
              Отмена
            </button>
            <button 
              className="create-button" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Создание...' : 'Создать'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCategoryModal; 